import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft, CheckCircle } from 'lucide-react';

import AuthLayout from '@/components/auth/AuthLayout';
import Input from '@/components/common/Input';
import Button from '@/components/common/Button';
import Alert from '@/components/common/Alert';
import { sendPasswordReset } from '@/services/authService';

const schema = z.object({
  email: z.string().email('Please enter a valid email address.'),
});

type FormValues = z.infer<typeof schema>;

export default function ForgotPasswordPage() {
  const [sent,        setSent]        = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [sentEmail,   setSentEmail]   = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  const onSubmit = async (values: FormValues) => {
    setServerError(null);
    try {
      await sendPasswordReset(values.email);
      setSentEmail(values.email);
      setSent(true);
    } catch (err) {
      setServerError((err as Error).message);
    }
  };

  if (sent) {
    return (
      <AuthLayout title="Check your email">
        <div className="text-center space-y-4">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-100">
            <CheckCircle className="h-8 w-8 text-emerald-600" />
          </div>
          <div>
            <p className="text-sm text-surface-600">
              We've sent a password reset link to:
            </p>
            <p className="font-medium text-surface-900 mt-1">{sentEmail}</p>
          </div>
          <p className="text-sm text-surface-500">
            Check your inbox and follow the link to reset your password.
            The link will expire in 1 hour.
          </p>
          <p className="text-xs text-surface-400">
            Didn't receive it? Check your spam folder or{' '}
            <button
              onClick={() => setSent(false)}
              className="text-primary-600 hover:underline"
            >
              try again
            </button>.
          </p>
          <Link
            to="/login"
            className="inline-flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 font-medium mt-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to sign in
          </Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Reset your password"
      subtitle="Enter your email and we'll send you a reset link."
    >
      <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">

        {serverError && (
          <Alert variant="error" message={serverError} />
        )}

        <Input
          label="Email address"
          type="email"
          autoComplete="email"
          autoFocus
          required
          leftIcon={<Mail className="h-4 w-4" />}
          error={errors.email?.message}
          {...register('email')}
        />

        <Button
          type="submit"
          fullWidth
          size="lg"
          loading={isSubmitting}
        >
          Send reset link
        </Button>

        <Link
          to="/login"
          className="flex items-center justify-center gap-2 text-sm text-surface-500 hover:text-surface-700"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to sign in
        </Link>

      </form>
    </AuthLayout>
  );
}
